import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Plus, X, Search, MessageCircle, Timer, Trophy,
  Book, Users, Award, Settings, Star, BookPlus, BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useChats } from '@/hooks/useChats';
import { useIsMobile } from '@/hooks/use-mobile';

interface QuickAction {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  href: string;
  badge?: string;
}

export function QuickActionFAB() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getTotalUnreadCount } = useChats();
  const isMobile = useIsMobile();
  const overlayRef = useRef<HTMLDivElement>(null);

  const unreadCount = getTotalUnreadCount();

  // Close on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const hideOnRoutes = ['/auth', '/onboarding'];
  if (!user || hideOnRoutes.some(route => location.pathname.startsWith(route))) {
    return null;
  }

  const messageBadge = unreadCount > 0
    ? (unreadCount >= 9 ? '9+' : unreadCount.toString())
    : undefined;

  // On mobile, FAB is the primary hub. On desktop, it's just quick actions.
  const mobileActions: QuickAction[] = [
    { icon: Search, label: 'Search', href: '/search' },
    { icon: MessageCircle, label: 'Messages', href: '/inbox', badge: messageBadge },
    { icon: Timer, label: 'Focus Timer', href: '/focus' },
    { icon: Trophy, label: 'Achievements', href: '/achievements' },
    { icon: Book, label: 'Series Tracker', href: '/series-tracker' },
    { icon: Users, label: 'Dino Duos', href: '/buddy-reads' },
    { icon: Award, label: 'Book Awards', href: '/awards' },
    { icon: BarChart3, label: 'Monthly Report', href: '/monthly-report' },
    { icon: Settings, label: 'Settings', href: '/settings' },
  ];

  const desktopActions: QuickAction[] = [
    { icon: Star, label: 'Rate Book', href: '/' },
    { icon: BookPlus, label: 'Add Book', href: '/discover' },
    { icon: Timer, label: 'Focus Session', href: '/focus' },
  ];

  const actions = isMobile ? mobileActions : desktopActions;

  const handleAction = (href: string) => {
    setIsOpen(false);
    navigate(href);
  };

  return (
    <>
      {/* Backdrop overlay */}
      {isOpen && (
        <div
          ref={overlayRef}
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <div className="fab-position flex flex-col-reverse items-end gap-2">
        {/* Action grid (mobile) / Action list (desktop) */}
        {isOpen && isMobile && (
          <div className="fixed bottom-0 left-0 right-0 z-50 pb-[env(safe-area-inset-bottom)] animate-in slide-in-from-bottom duration-300">
            <div className="mx-3 mb-20 bg-card rounded-2xl shadow-2xl border border-border/50 overflow-hidden">
              <div className="grid grid-cols-4 gap-1 p-3">
                {actions.map((action) => {
                  const isActive = location.pathname === action.href;
                  return (
                    <button
                      key={action.href}
                      onClick={() => handleAction(action.href)}
                      className={cn(
                        "relative flex flex-col items-center gap-1.5 py-3 px-1 rounded-xl transition-all duration-150",
                        "touch-target tap-feedback",
                        isActive
                          ? "bg-primary/15 text-primary"
                          : "text-foreground active:bg-muted"
                      )}
                    >
                      <div className="relative">
                        <action.icon className="w-5 h-5" />
                        {action.badge && (
                          <Badge
                            variant="destructive"
                            className="absolute -top-2 -right-3 h-4 min-w-4 text-[9px] px-1 flex items-center justify-center"
                          >
                            {action.badge}
                          </Badge>
                        )}
                      </div>
                      <span className="text-[10px] font-medium leading-tight text-center">
                        {action.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Desktop action list */}
        {isOpen && !isMobile && actions.map((action, index) => (
          <div
            key={action.label}
            className={cn(
              "flex items-center gap-3 animate-in slide-in-from-bottom fade-in",
              "duration-200"
            )}
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <span className="bg-card text-card-foreground px-3 py-2 rounded-xl shadow-lg text-sm font-medium whitespace-nowrap">
              {action.label}
            </span>
            <Button
              size="icon"
              className="h-12 w-12 rounded-full shadow-lg touch-target tap-feedback bg-primary hover:bg-primary/90 text-primary-foreground"
              onClick={() => handleAction(action.href)}
            >
              <action.icon className="h-5 w-5" />
            </Button>
          </div>
        ))}

        {/* Main FAB button */}
        <Button
          size="icon"
          className={cn(
            "h-14 w-14 rounded-full shadow-xl transition-all duration-200 touch-target-lg",
            "bg-primary hover:bg-primary/90 text-primary-foreground",
            "active:scale-95",
            isOpen && "rotate-45 bg-muted text-foreground hover:bg-muted/90"
          )}
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Plus className="h-6 w-6" />
          )}

          {/* Unread badge on FAB when closed (mobile only) */}
          {!isOpen && messageBadge && isMobile && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 min-w-5 text-[10px] px-1 flex items-center justify-center animate-pulse"
            >
              {messageBadge}
            </Badge>
          )}
        </Button>
      </div>
    </>
  );
}
